import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { calcInitial, mediaQueriesBP } from "@solveindustrial/Utilities/variables";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export const enum fields {
    message = "message",
    scheduleStart = "scheduleStart",
    scheduleEnd = "scheduleEnd",
    customCSS = "customCSS",
    backgroundColor = "backgroundColor",
    fontColor = "fontColor",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.message]: string;
        [fields.scheduleStart]: Date;
        [fields.scheduleEnd]: Date;
        [fields.customCSS]: string;
        [fields.backgroundColor]: string;
        [fields.fontColor]: string;
    };
}

export const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const session = state.context.session;
    const todaysDate = new Date();
    const { scheduleStart, scheduleEnd } = ownProps.fields;

    return {
        startCondition: scheduleStart ? todaysDate < new Date(scheduleStart) : false,
        endCondition: scheduleEnd ? todaysDate > new Date(scheduleEnd) : false,
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest,
    };
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasShellContext;

export interface HeaderAlertBannerWidgetStyles {
    wrapper?: InjectableCss;
    message?: InjectableCss;
}

export const headerAlertBannerWidgetStyles: HeaderAlertBannerWidgetStyles = {
    wrapper: {
        css: css`
            position: relative;
            padding: 8px 0;
            z-index: 0;

            ${mediaQueriesBP.maxMD} {
                padding: 8px 16px;
            }

            &::before {
                content: "";
                position: absolute;
                bottom: 0;
                left: ${calcInitial};
                right: ${calcInitial};
                top: 0;
                z-index: -1;
            }
        `,
    },
    message: {
        css: css`
            & > ${TypographyStyle} {
                &:last-child {
                    margin-bottom: 0;
                }
            }
        `,
    },
};

const defaultCustomCss = `& {
}

.alert__message {

}
`;

export const HeaderAlertBannerWidget = ({
    fields: { message, customCSS, backgroundColor, fontColor },
    startCondition,
    endCondition,
    shellContext: { isInShell },
    isAuthenticated,
}: Props) => {
    if (!isInShell && (!isAuthenticated || startCondition || endCondition)) {
        return null;
    }

    const styles = useMergeStyles("richContent", headerAlertBannerWidgetStyles);
    const customCssWrapper = {
        css: css`
            ${styles.wrapper?.css}
            background-color: ${getColor(backgroundColor)};
            color: ${getColor(fontColor)} !important;

            & * {
                color: ${getColor(fontColor)} !important;
            }

            &::before {
                background-color: ${getColor(backgroundColor)};
            }

            ${customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="alert__message" {...styles.message}>
                {parse(message, parserOptions)}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withIsInShell(HeaderAlertBannerWidget)) as any,
    definition: {
        displayName: "Header Alert Banner",
        group: "Header",
        icon: "image-landscape",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.scheduleStart,
                displayName: "Schedule Start Date+Time",
                editorTemplate: "CustomDateTimeField",
                defaultValue: null,
                fieldType: "Translatable",
                tab: basicTab,
                instructions:
                    "Remember the date/time selected is using EST. The value selected for your time zone is: {0} / {1}",
                timezone: -5,
            } as any,
            {
                name: fields.scheduleEnd,
                displayName: "Schedule End Date+Time",
                editorTemplate: "CustomDateTimeField",
                defaultValue: null,
                fieldType: "Translatable",
                tab: basicTab,
                instructions:
                    "Remember the date/time selected is using EST. The value selected for your time zone is: {0} / {1}",
                timezone: -5,
            } as any,

            {
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "#ffcc00",
            },
            {
                name: fields.fontColor,
                editorTemplate: "ColorPickerField",
                displayName: "Font Color",
                defaultValue: "#000000",
            },

            {
                name: fields.message,
                displayName: "Alert Message",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                collapsedToolbarButtons: {
                    moreText: {
                        buttons: ["bold", "italic", "link"],
                        buttonsVisible: 4,
                    },
                },
                expandedToolbarButtons: {
                    moreParagraph: {
                        buttons: ["alignLeft", "alignCenter", "alignRight"],
                        buttonsVisible: 3,
                    },
                    moreText: {
                        buttons: ["bold", "italic", "link"],
                        buttonsVisible: 4,
                    },
                    moreMisc: {
                        buttons: ["insertLink"],
                        buttonsVisible: 1,
                    },
                    code: {
                        buttons: [],
                        buttonsVisible: 0,
                    },
                    unredo: { buttons: ["undo", "redo"] },
                },
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : "The HTML contains invalid styles that prevent it from being saved";
                },
                isRequired: true,
                tab: basicTab,
            },

            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                defaultValue: defaultCustomCss,
                fieldType: "General",
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
                tab: advancedTab,
            },
        ],
    },
};

export default widgetModule;
